import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { LoadingPanel } from "../../LoadingPanel";
import { addNotificationWithTimeout } from '../../../app/addNotificationWithTimeout';
import { handleResponse } from '../../../app/handleResponse';
import { getCSRFToken } from '../../../app/getCSRFToken';
import { Button } from "@progress/kendo-react-buttons";
import { AutoComplete } from "@progress/kendo-react-dropdowns";
import { Label } from "@progress/kendo-react-labels";
import { CommandCell } from '../../forms/CommandCell';
import {
    Grid,
    GridColumn as Column,
} from "@progress/kendo-react-grid";

import './Login.scss';

export const AdministratorLoginShortingDisabledPerformers = (props) => {
    const dispatch = useDispatch();

    const editField = "inEdit";

    const [loginShortingDisabledPerformers, setLoginShortingDisabledPerformers] = useState([]);
    const [areLoginShortingDisabledPerformersLoading, setAreLoginShortingDisabledPerformersLoading] = useState(true);
    const [loadingError, setLoadingError] = useState("");
    const [performers, setPerformers] = useState([]);
    const [performerName, setPerformerName] = useState(null);
    const [arePerformersLoading, setArePerformersLoading] = useState(false);

    const autocompleteTimeout = useRef();
    const autocompleteTimeoutDelay = 500; //Milliseconds

    const getLoginShortingDisabledPerformers = () => {
        setAreLoginShortingDisabledPerformersLoading(true);

        const dataItem = {
            loginKey: props.loginKey,
        };

        fetch(`/api/administrator/loginshortingdisabledperformers`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                setAreLoginShortingDisabledPerformersLoading(false);
                setLoginShortingDisabledPerformers(result);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);

                setAreLoginShortingDisabledPerformersLoading(false);
                setLoadingError(error.message);
            });
    }

    const deleteLoginShortingDisabledPerformer = (loginShortingDisabledPerformerKey) => {
        const dataItem = {
            loginShortingDisabledPerformerKey: loginShortingDisabledPerformerKey
        }

        fetch("/api/administrator/loginshortingdisabledperformer",
            {
                method: "DELETE",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", "Deleted disabled performer successfully");

                getLoginShortingDisabledPerformers();
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    }

    const savePerformer = () => {
        const performer = performers.find(element => element.name === performerName);

        if (!performer) {
            addNotificationWithTimeout(dispatch, "error", "You must select a performer");
            return;
        }

        const dataItem = {
            loginKey: props.loginKey,
            performerKey: performer.performerKey,
        }

        fetch("/api/administrator/loginshortingdisabledperformer",
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", "Disabled performer successfully");

                getLoginShortingDisabledPerformers();
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    }

    const discard = (dataItem) => {
        const newLoginShortingDisabledPerformers = [...loginShortingDisabledPerformers];
        newLoginShortingDisabledPerformers.splice(0, 1);
        setLoginShortingDisabledPerformers(newLoginShortingDisabledPerformers);
    };

    const remove = (dataItem) => {
        deleteLoginShortingDisabledPerformer(dataItem.loginShortingDisabledPerformerKey);
    };

    const MyCommandCell = (props) => (
        <CommandCell
            {...props}
            canEdit={false}
            remove={remove}
            discard={discard}
            editField={editField}
            keyField="loginShortingDisabledPerformerKey"
        />
    );

    const itemChange = (event) => {
        const field = event.field || "";
        const newLoginShortingDisabledPerformers = loginShortingDisabledPerformers.map((item) =>
            item.loginShortingDisabledPerformerKey === event.dataItem.loginShortingDisabledPerformerKey
                ? { ...item, [field]: event.value }
                : item
        );
        setLoginShortingDisabledPerformers(newLoginShortingDisabledPerformers);
    };

    const performerChanged = (event) => {
        const value = event.value;

        clearTimeout(autocompleteTimeout.current);

        if (!value || value.length < 2) {
            setPerformers([]);
            setArePerformersLoading(false);
            setPerformerName(null);
            return;
        }

        autocompleteTimeout.current = setTimeout(() => {
            const dataItem = {
                name: value,
                onlyActive: true,
            };

            fetch(`/api/administrator/performers`,
                {
                    method: "POST",
                    headers: {
                        'Content-Type': 'application/json',
                        "X-XSRF-TOKEN": getCSRFToken(),
                    },
                    body: JSON.stringify(dataItem),
                })
                .then(handleResponse)
                .then(result => {
                    setPerformers(result.performers);
                    setPerformerName(value);
                })
                .catch(error => {
                    addNotificationWithTimeout(dispatch, "error", error.message);
                })
                .finally(() => {
                    setArePerformersLoading(false);
                });
        }, autocompleteTimeoutDelay);

        setArePerformersLoading(true);
    };

    useEffect(() => {
        if (props.loginKey) {
            getLoginShortingDisabledPerformers();
        }
    }, []);

    return (
        <div>
            <div className="administratorLoginShortingDisabledPerformerAdd">
                <Label>Add performer:</Label>

                <AutoComplete
                    id={"performer"}
                    name={"performer"}
                    data={performers}
                    placeholder="e.g. Taylor Swift"
                    textField="name"
                    onChange={performerChanged}
                    loading={arePerformersLoading}
                />

                <Button
                    themeColor={"primary"}
                    type={"button"}
                    onClick={savePerformer}
                >
                    Save
                </Button>
            </div>
            {areLoginShortingDisabledPerformersLoading ? <LoadingPanel /> : (loadingError === "" ? (
                <Grid
                    data={loginShortingDisabledPerformers}
                    onItemChange={itemChange}
                    editField={editField}
                    dataItemKey={"loginShortingDisabledPerformerKey"}
                    resizable={true}
                    scrollable="none"
                >
                    <Column field="performerName" title={"Performer"} />
                    <Column cell={MyCommandCell} width="150px" />
                </Grid>
            ) : <></>)
            }
        </div>
    );
}